import { Group, Text, Tooltip, UnstyledButton } from '@mantine/core'
import { faPenToSquare } from '@fortawesome/sharp-duotone-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { formatTaskDate } from '../../utils/formatters'

export interface TaskStatusRowProps {
  taskType: string
  // MC1's own status. The card never rewrites it optimistically — a hold being edited
  // is not a hold until the save lands.
  status: string
  targetDate: string | null | undefined
  // False for MC1's terminal statuses, which the card can set nothing on, and for a hold
  // still in force, which the on-hold proc refuses to change. On a held task whose hold
  // date has passed, the pencil reopens the hold editors.
  // Hold is the only status it can set, so it opens those editors directly rather than
  // offering a choice.
  canEdit: boolean
  onEdit: () => void
}

export function TaskStatusRow({ taskType, status, targetDate, canEdit, onEdit }: Readonly<TaskStatusRowProps>) {
  return (
    <Group gap="md" wrap="nowrap">
      {/* MC1 owns the target date and the update endpoint cannot set it, so it
          is read-only here — only the hold date below carries a pencil. A task
          MC1 sent no target date for reads "No Record", the app's convention for
          an absent value, rather than leaving the label dangling. */}
      <Text fz="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
        Target Date:{' '}
        {formatTaskDate(targetDate) ?? (
          <Text span inherit c="var(--link-color)" fs="italic">
            No Record
          </Text>
        )}
      </Text>
      <Group gap={6} wrap="nowrap">
        <Text fz="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
          Status: {status}
        </Text>
        {canEdit && (
          <Tooltip label="Edit status" withArrow>
            <UnstyledButton c="brand.7" lh={1} aria-label={`Edit status of task ${taskType}`} onClick={onEdit}>
              <FontAwesomeIcon icon={faPenToSquare} size="sm" />
            </UnstyledButton>
          </Tooltip>
        )}
      </Group>
    </Group>
  )
}
